// CategoryOnshapePanel — THE category-level Onshape block, shared by BOTH sides
// (UI_VISION phases 2-4). Catalog categories and product categories want the
// identical thing; the only differences are the PATCH endpoint and whether the
// section can ever be #NA. Flags, not forks — so this lives here rather than
// being typed a second time in ProductsView.
//
// A category points at the ASSEMBLY that collects its items — Alex's
// "> BOM > All Config" element — NOT at a document. He caught the earlier
// version of this (2026-07-21): "I do not believe we need to ref a document but
// an assembly." Right on both counts. The old `onshape_doc_ref` held nothing but
// the did/wid of its own families' refs (verified: 100% match across all 22
// families), and a document ref cannot reach
// /api/v9/assemblies/d/{did}/w/{wid}/e/{eid}/bom — which is the whole point of a
// category-level link. `onshape_doc_ref` is dormant.
//
// EVERY level, both sides. Assemblies NEST, so a parent's assembly is the one
// containing its children's — which dissolved the old "one L3 = one document"
// limit rather than widening it.
//
//   patchUrl  — where a save goes. `/api/catalog/categories/:id`,
//               `/api/products/categories/:id` or a project function; all three
//               accept `onshape_ref` and validate it identically. One field
//               name is the point — this panel is shared, so a per-endpoint
//               name would fork it (Alex, 2026-07-28).
//   applicable — false renders `#NA` with no editor. CATALOG passes
//               `costClass !== 'HR'` (labour can never be modelled in CAD).
//               PRODUCTS always pass true — Alex, deciding phase 4: "from a
//               small product to the biggest product, we always have an
//               opportunity to have Onshape. At least one day or another."
//               There is no product that can be ruled out in advance.
//   storageKey — per-surface collapse memory, so collapsing the catalog
//               category section doesn't also collapse the product one.
//   title      — defaults to '3D Model' for every consumer. The old
//               "(category)"/"(family)"/"(function)" qualifier was dropped
//               (Alex: "Is it really useful"): it repeated the header row
//               directly above it, and only one such section is ever on screen
//               at a time, so there was nothing to disambiguate.

import { useState, useEffect } from 'react';
import OnshapeSection from './OnshapeSection.jsx';
import { WARN } from '../icons.js';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED } from '../theme-tokens.js';

export default function CategoryOnshapePanel({
  categoryId,
  onshapeAssemblyRef,
  patchUrl,
  title = '3D Model',
  applicable = true,
  naReason = null,
  storageKey = 'category',
  onSaved,
}) {
  const [draft, setDraft] = useState(onshapeAssemblyRef || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(onshapeAssemblyRef || ''); setError(null); }, [categoryId, onshapeAssemblyRef]);

  const dirty = draft !== (onshapeAssemblyRef || '');
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(patchUrl, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onshape_ref: draft.trim() }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Save failed'); }
      await onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnshapeSection
      onshapeRef={onshapeAssemblyRef || null}
      title={title}
      storageKey={storageKey}
      applicable={applicable}
      naReason={naReason}
      // An assembly is a COLLECTION, not a part: no single thumbnail and no one
      // model to view, so both stay off rather than spending a call on an
      // arbitrary member. (The BOM read is a separate, explicit action.)
      showThumbnail={false}
      showViewer={false}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
          Onshape assembly (did/wid/eid)
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="did/wid/eid"
            style={{ fontSize: 'var(--fs-small)', padding: '3px 6px', border: '1px solid var(--th-border-subtle)', borderRadius: 3, width: 340, fontFamily: 'var(--ff-mono)' }}
          />
        </label>
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{ ...BTN_PRIMARY, ...((!dirty || saving) ? BTN_DISABLED : null) }}
        >{saving ? 'Saving…' : 'Save'}</button>
        {/* COS-1(b): every grid row already gets Save+Cancel — this editor was
            the outlier, offering only Save with no way back to the stored value
            short of retyping it. */}
        {dirty && !saving && (
          <button onClick={() => { setDraft(onshapeAssemblyRef || ''); setError(null); }} style={BTN_SECONDARY}>Cancel</button>
        )}
        {error && <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-status-deprecated)' }}>{WARN} {error}</span>}
      </div>
    </OnshapeSection>
  );
}
